import { useState, useEffect } from "react";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Checkbox,
    Input,
    Spinner,
} from "@heroui/react";
import { useNavigate, useParams } from "react-router-dom";
import { useFetchData, usePostData } from "@/hooks";
import { showToast } from "@/utils";
import {
    EmptyState,
    ErrorState,
    ThemedText,
    PageHeader,
} from "@/components/ui";
import {
    LuArrowLeft,
    LuInfo,
    LuSave,
    LuSearch,
    LuShieldOff,
    LuX,
} from "react-icons/lu";
import { useUser } from "@/context/UserContext";

const RolePermissions = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useUser();

    const [checkedIds, setCheckedIds] = useState(new Set());
    const [showLegend, setShowLegend] = useState(true);
    const [search, setSearch] = useState("");

    const {
        data: permData,
        isLoading,
        error,
        refetch,
    } = useFetchData(`roles/${id}/permissions`);

    const groups = permData?.groups;
    const legend = permData?.legend;

    const filteredGroups = search.trim()
        ? groups?.filter((g) =>
              g.label.toLowerCase().includes(search.toLowerCase()),
          )
        : groups;

    const { data: roleData } = useFetchData(`roles/${id}/edit`);
    const roleName = roleData?.name || "";

    useEffect(() => {
        if (groups) {
            const assignedIds = groups.flatMap((g) =>
                g.permissions.filter((p) => p.assigned).map((p) => p.id),
            );
            setCheckedIds(new Set(assignedIds));
        }
    }, [groups]);

    const togglePermission = (permId) => {
        setCheckedIds((prev) => {
            const next = new Set(prev);
            if (next.has(permId)) {
                next.delete(permId);
            } else {
                next.add(permId);
            }
            return next;
        });
    };

    const toggleGroup = (groupPerms) => {
        const allChecked = groupPerms.every((p) => checkedIds.has(p.id));
        setCheckedIds((prev) => {
            const next = new Set(prev);
            if (allChecked) {
                groupPerms.forEach((p) => next.delete(p.id));
            } else {
                groupPerms.forEach((p) => next.add(p.id));
            }
            return next;
        });
    };

    const mutation = usePostData(`roles/${id}/sync-permissions`);

    const handleSave = () => {
        mutation.mutate(
            { permission_ids: Array.from(checkedIds) },
            {
                method: "put",
                onSuccess: () => {
                    showToast(
                        "success",
                        "Berhasil",
                        "Permission role berhasil diperbarui.",
                    );
                    const isOwnRole = user?.roles?.includes(roleName);
                    if (isOwnRole) {
                        window.location.href = "/role";
                    } else {
                        navigate("/role");
                    }
                },
                onError: () => {
                    showToast(
                        "error",
                        "Gagal",
                        "Terjadi kesalahan saat menyimpan permission.",
                    );
                },
            },
        );
    };

    if (error) {
        return (
            <ErrorState
                title="Gagal Memuat Data"
                description="Terjadi kesalahan saat memuat data permission."
                onAction={refetch}
            />
        );
    }

    return (
        <div className="mx-auto flex max-w-5xl flex-col gap-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div className="flex gap-4">
                    <Button
                        isIconOnly
                        variant="light"
                        onPress={() => navigate(-1)}
                    >
                        <LuArrowLeft size={20} />
                    </Button>
                    <PageHeader
                        title={`Kelola Permission${roleName ? ` — ${roleName}` : ""}`}
                        breadcrumbs={[
                            { label: "Dashboard", href: "/" },
                            { label: "HR" },
                            { label: "Hak Akses", href: "/role" },
                            { label: "Kelola Permission" },
                        ]}
                    />
                </div>
                <div className="flex items-center gap-3">
                    {groups && (
                        <Checkbox
                            size="sm"
                            isSelected={groups
                                .flatMap((g) => g.permissions)
                                .every((p) => checkedIds.has(p.id))}
                            isIndeterminate={
                                !groups
                                    .flatMap((g) => g.permissions)
                                    .every((p) => checkedIds.has(p.id)) &&
                                groups
                                    .flatMap((g) => g.permissions)
                                    .some((p) => checkedIds.has(p.id))
                            }
                            onValueChange={(checked) => {
                                const allIds = groups
                                    .flatMap((g) => g.permissions)
                                    .map((p) => p.id);
                                setCheckedIds(
                                    checked ? new Set(allIds) : new Set(),
                                );
                            }}
                        >
                            <ThemedText size="sm">Pilih Semua</ThemedText>
                        </Checkbox>
                    )}
                    {!showLegend && (
                        <Button
                            isIconOnly
                            variant="flat"
                            size="sm"
                            onPress={() => setShowLegend((v) => !v)}
                            title="Keterangan aksi"
                        >
                            <LuInfo size={16} />
                        </Button>
                    )}
                    <Button
                        color="primary"
                        size="sm"
                        isLoading={mutation.isLoading}
                        onPress={handleSave}
                        startContent={!mutation.isLoading && <LuSave />}
                    >
                        Simpan
                    </Button>
                </div>
            </div>

            {/* Legend */}
            {showLegend && legend?.length > 0 && (
                <div className="border-default-200 bg-default-50 rounded-lg border px-5 py-3">
                    <div className="mb-1.5 flex items-center justify-between">
                        <ThemedText
                            size="xs"
                            variant="secondary"
                            className="font-medium"
                        >
                            Keterangan aksi:
                        </ThemedText>
                        <Button
                            isIconOnly
                            variant="light"
                            size="sm"
                            className="h-5 w-5 min-w-0"
                            onPress={() => setShowLegend(false)}
                        >
                            <LuX size={12} />
                        </Button>
                    </div>
                    <ul className="list-disc space-y-0.5 pl-4">
                        {legend.map(({ label, description }) => (
                            <li key={label}>
                                <ThemedText size="xs" variant="secondary">
                                    <span className="text-foreground font-medium">
                                        {label}
                                    </span>{" "}
                                    — {description}
                                </ThemedText>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Search */}
            {!isLoading && groups?.length > 0 && (
                <Input
                    placeholder="Cari modul permission..."
                    value={search}
                    onValueChange={setSearch}
                    startContent={
                        <LuSearch size={16} className="text-default-400" />
                    }
                    isClearable
                    onClear={() => setSearch("")}
                    size="sm"
                    // variant="lat"
                    className="max-w-sm"
                />
            )}

            {/* Permission Groups */}
            {isLoading ? (
                <div className="flex h-48 w-full items-center justify-center">
                    <Spinner label="Memuat permission..." color="primary" />
                </div>
            ) : !groups?.length ? (
                <EmptyState
                    title="Belum Ada Permission"
                    description="Tidak ada permission yang tersedia untuk dikelola."
                    icon={LuShieldOff}
                />
            ) : !filteredGroups?.length ? (
                <EmptyState
                    title="Tidak Ditemukan"
                    description={`Tidak ada modul yang cocok dengan "${search}".`}
                    icon={LuShieldOff}
                />
            ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {filteredGroups.map((group) => {
                        const allChecked = group.permissions.every((p) =>
                            checkedIds.has(p.id),
                        );
                        const someChecked = group.permissions.some((p) =>
                            checkedIds.has(p.id),
                        );

                        return (
                            <Card
                                key={group.key}
                                className="border-default-200 border"
                                shadow="none"
                            >
                                <CardHeader className="bg-default-100/70 px-6 py-3">
                                    <div className="flex w-full items-center justify-between">
                                        <ThemedText className="font-semibold">
                                            {group.label}
                                        </ThemedText>
                                        <Checkbox
                                            size="sm"
                                            isSelected={allChecked}
                                            isIndeterminate={
                                                !allChecked && someChecked
                                            }
                                            onValueChange={() =>
                                                toggleGroup(group.permissions)
                                            }
                                        >
                                            <ThemedText
                                                size="xs"
                                                variant="secondary"
                                            >
                                                Pilih Semua
                                            </ThemedText>
                                        </Checkbox>
                                    </div>
                                </CardHeader>
                                <CardBody className="px-6 py-4">
                                    <div className="grid grid-cols-2 gap-3">
                                        {group.permissions.map((perm) => (
                                            <Checkbox
                                                key={perm.id}
                                                size="sm"
                                                isSelected={checkedIds.has(
                                                    perm.id,
                                                )}
                                                onValueChange={() =>
                                                    togglePermission(perm.id)
                                                }
                                            >
                                                {perm.label}
                                            </Checkbox>
                                        ))}
                                    </div>
                                </CardBody>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default RolePermissions;
